import React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';

import {
  AgencyLegalError,
  AgencyLegalLoading,
  AgencyLegalShell,
  LegalDocumentMeta,
  LegalRouteParams,
  Section,
  SupportEmailLink,
  TERMS_OF_USE_LAST_REVISED,
  agencyLegalStyles as styles,
  useAgencyLegalDisplayName,
} from './agencyLegalShared';

export default function AgencyTermsOfUsePage({
  match,
}: RouteComponentProps<LegalRouteParams>) {
  const { crmToken } = match.params;
  const privacyPolicyPath = `/agencies/${crmToken}/privacy-policy`;
  const { displayName, isLoading, error } = useAgencyLegalDisplayName(crmToken);

  if (isLoading) {
    return <AgencyLegalLoading />;
  }

  if (error || !displayName) {
    return <AgencyLegalError />;
  }

  return (
    <AgencyLegalShell>
      <h1 style={styles.title}>Terms of Service {displayName}</h1>
      <LegalDocumentMeta lastRevised={TERMS_OF_USE_LAST_REVISED} />

      <hr style={styles.divider} />

      <Section title="ACCEPTANCE OF TERMS">
        <p style={styles.paragraph}>
          This mobile application (&quot;the App&quot;) is provided on behalf of your local government
          agency. By downloading, installing, or using the App, you agree to these Terms of Service. If
          you do not agree, do not install or use the App. These Terms may be updated from time to time;
          continued use constitutes acceptance.
        </p>
      </Section>

      <Section title="ABOUT THE APP">
        <p style={styles.paragraph}>
          The App allows residents to submit non-emergency service requests to their local government,
          track request status, and receive updates. Available features may vary by agency.
        </p>
      </Section>

      <Section title="ELIGIBILITY">
        <p style={styles.paragraph}>
          You must be at least 13 years of age to use the App. The App does not knowingly collect personal
          information from children under 13.
        </p>
      </Section>

      <Section title="ACCOUNT REGISTRATION">
        <p style={styles.paragraph}>
          Some features require an account. You agree to provide accurate and complete information. False,
          inaccurate, or offensive information may result in account suspension or termination.
        </p>
      </Section>

      <Section title="ANONYMOUS SUBMISSIONS">
        <p style={styles.paragraph}>
          Whether anonymous submission is permitted depends on your agency&apos;s configuration and
          applicable law. Some jurisdictions require submitter identification for certain request types,
          including code enforcement complaints.
        </p>
      </Section>

      <Section title="PUBLIC RECORDS">
        <p style={styles.paragraph}>
          Service requests submitted through the App may constitute public records subject to applicable
          local, state, and federal law. Submitting a request does not guarantee confidentiality. Personal
          information you provide is used to respond to your request and may be shared with agency staff
          involved in its resolution. See our{' '}
          <Link to={privacyPolicyPath}>Privacy Policy</Link> for more information.
        </p>
      </Section>

      <Section title="YOUR CONDUCT">
        <p style={styles.paragraph}>
          You agree not to submit content that is false, offensive, or harassing, includes others&apos;
          personal information without consent, or violates applicable law. The App administrator reserves
          the right to remove submissions that violate these standards.
        </p>
      </Section>

      <Section title="NON-EMERGENCY USE ONLY">
        <p style={styles.paragraph}>
          This App is for non-emergency requests only. Call 911 for emergencies. Response times vary by
          agency.
        </p>
      </Section>

      <Section title="DISCLAIMER OF WARRANTIES">
        <p style={styles.paragraph}>
          The App is provided &quot;as is&quot; without warranties of any kind. Use is at your own risk.
        </p>
      </Section>

      <Section title="LIMITATION OF LIABILITY">
        <p style={styles.paragraph}>
          To the fullest extent permitted by law, neither the App provider nor your local government agency
          shall be liable for indirect, incidental, or consequential damages arising from your use of the
          App.
        </p>
      </Section>

      <Section title="GOVERNING LAW">
        <p style={styles.paragraph}>
          These Terms are governed by the laws of the applicable jurisdiction.
        </p>
      </Section>

      <Section title="CONTACT">
        <p style={styles.paragraph}>
          Comcate Software, Inc. 9450 SW Gemini Dr PMB 61173
          <br />
          Beaverton, Oregon 97008-7105 US
          <br />
          Email: <SupportEmailLink /> Phone: <a href="tel:+14156321249">(415) 632-1249</a>
        </p>
      </Section>
    </AgencyLegalShell>
  );
}
